import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  invokeAiModel,
  type AiModelInvokeResult,
  type AiModelSummary,
  type StockChoice,
} from "../api/client";
import { ModelResultView } from "./ModelResultView";
import { StockStickerPicker } from "./StockStickerPicker";

type Props = {
  model: AiModelSummary;
  initialStock?: string | null;
  onClose: () => void;
};

export function ModelResultModal({ model, initialStock, onClose }: Props) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [result, setResult] = useState<AiModelInvokeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const stockChoices: StockChoice[] = model.stock_choices ?? [];
  const [stock, setStock] = useState<string | null>(
    initialStock ?? model.default_stock ?? stockChoices[0]?.symbol ?? null,
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const body = stock ? { stock } : undefined;
    invokeAiModel(model.id, body)
      .then((data) => {
        if (!cancelled) setResult(data);
      })
      .catch(() => {
        if (!cancelled) {
          setError("We could not run this model right now. Please try again.");
          setResult(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [model.id, stock]);

  return createPortal(
    <div
      className="model-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="model-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="model-modal-header">
          <div>
            <p className="eyebrow">Results</p>
            <h2 id={titleId} className="model-modal-title">
              {result?.model_name || model.name}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="model-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="model-modal-body">
          {stockChoices.length > 0 && stock && (
            <StockStickerPicker
              choices={stockChoices}
              value={stock}
              disabled={loading}
              onChange={setStock}
            />
          )}

          {loading && <p className="muted page-loading">Running model…</p>}

          {error && !loading && (
            <p className="model-run-error" role="alert">
              {error}
            </p>
          )}

          {!loading && !error && result && (
            <ModelResultView result={result} onResultChange={setResult} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
